import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Waves, CalendarDays } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import ParallaxImage from "@/components/ParallaxImage";
import EditorialMarquee from "@/components/EditorialMarquee";

const resetPoints = [
  "Regulate your nervous system to handle stress effortlessly",
  "Harness breathwork and cold exposure to unlock resilience and focus",
  "Reprogram your mindset for lasting transformation",
  "Walk away with a step-by-step plan to create powerful, lasting change",
];

const dipExpectations = [
  "Introductions and calming exercises",
  "Time in the water together",
  "Support from a trained instructor and the community",
  "A good chat with other people",
  "Tea, coffee and biscuits afterwards",
];

const whyUs = [
  "Switch off from the constant noise of life and recharge in nature",
  "Take time out for yourself and meet like-minded people",
  "Unplug and de-stress in the full-day experience",
  "Unlock the power and potential within you",
  "Discover nature's endless benefits",
];

const Events = () => (
  <main data-testid="events-page">
    <PageHero
      testId="events-hero"
      overline="Events & Immersions"
      lines={["For everyone.", "Guided, always."]}
      sub="Whether it is a workshop or a full day of self exploration, the RX4 team will guide and support you, teaching principles and processes to build a more fulfilling life."
      image="/images/rx4/community-dip.webp"
      alt="RX4 community cold water dip with flag"
    />

    {/* UPCOMING */}
    <section className="px-6 md:px-12 lg:px-20 py-14 max-w-7xl mx-auto w-full" data-testid="upcoming-events-strip">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-ink-soft border border-white/10 rounded-2xl px-8 py-7">
          <div className="flex items-center gap-4">
            <CalendarDays size={22} className="text-brand" strokeWidth={1.75} />
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Upcoming events</p>
              <p className="font-display font-semibold text-bone mt-1" data-testid="upcoming-events-status">No events at the moment. New dates are announced on social media first.</p>
            </div>
          </div>
          <Link to="/contact" data-testid="upcoming-notify-btn" className="text-xs font-medium uppercase tracking-widest text-brand hover:text-brand-hover transition-colors duration-300 whitespace-nowrap">
            Register your interest
          </Link>
        </div>
      </Reveal>
    </section>

    {/* RESET WORKSHOP */}
    <section className="bg-ink-soft border-y border-white/5" data-testid="reset-workshop-section">
      <div className="section-shell">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <Reveal className="lg:col-span-6">
            <p className="overline-label mb-4">Signature Workshop</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-bone leading-[1.05]">
              RX4 <span className="text-brand">RESET</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed max-w-xl">
              Feeling stuck, overwhelmed, or just out of sync? It is time for a RESET. An immersive, hands-on experience designed to rewire your stress response, master your mindset and build habits that stick. You will arrive feeling stuck. You will leave feeling unstoppable.
            </p>
            <ul className="mt-8 space-y-4">
              {resetPoints.map((r, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-300" data-testid={`reset-point-${i}`}>
                  <Check size={18} className="text-brand mt-1 shrink-0" strokeWidth={2.5} /> {r}
                </li>
              ))}
            </ul>
            <Link
              to="/apply"
              data-testid="reset-apply-btn"
              className="btn-primary group mt-10"
            >
              Embark on the journey
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6">
            <ParallaxImage
              src="/images/rx4/breathwork-firepit.jpg"
              alt="RX4 breathwork session around the fire pit"
              className="aspect-[4/5] rounded-2xl max-h-[70vh]"
              speed={0.12}
            />
          </Reveal>
        </div>
      </div>
    </section>

    <EditorialMarquee items={["FREE COMMUNITY DIPS", "EVERY SUNDAY AT ST BEES BEACH"]} />

    {/* COMMUNITY DIPS */}
    <section className="section-shell" data-testid="community-dips-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <Reveal className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-5">
            <Waves size={22} className="text-brand" strokeWidth={1.75} />
            <p className="overline-label">Free Community Dips</p>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-bone leading-snug">
            Every Sunday. St Bees Beach.
          </h2>
          <p className="mt-6 text-slate-400 leading-relaxed">
            We gather as a community for cold water immersion together. Our purpose is to encourage beginners to come along and feel supported in their cold exposure journey. Check social media each week for timings.
          </p>
          <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-brand">Free to join / All welcome</p>
          <div className="mt-8 rounded-2xl overflow-hidden border border-white/10">
            <img src="/images/rx4/dave-sea-dip.jpg" alt="Dave mid-dip at St Bees with the community behind" className="w-full aspect-[3/2] object-cover" loading="lazy" data-testid="community-dip-photo" />
          </div>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-7">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500 mb-5">What you can expect</p>
          <div className="bg-ink-soft border border-white/10 rounded-2xl overflow-hidden">
            {dipExpectations.map((d, i) => (
              <div key={i} data-testid={`dip-expectation-${i}`} className="flex items-baseline gap-6 border-b border-white/10 last:border-b-0 px-6 md:px-8 py-5 transition-colors duration-300 hover:bg-ink-lift">
                <span className="font-mono text-sm text-brand">0{i + 1}</span>
                <p className="text-base text-slate-300">{d}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>

    {/* WILD SAUNA */}
    <section className="bg-ink-soft border-y border-white/5" data-testid="wild-sauna-section">
      <div className="section-shell">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <Reveal className="lg:col-span-6">
            <p className="overline-label mb-4">The RX4 Wild Sauna</p>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-bone leading-snug">
              Heat, then plunge. The full contrast ritual.
            </h2>
            <p className="mt-6 text-slate-400 leading-relaxed max-w-xl">
              Our wood-fired wild sauna joins selected events and community sessions by the sea. Warm through, breathe deep, then take the plunge into cold water. It is the RX4 reset in its purest form.
            </p>
            <Link
              to="/contact"
              data-testid="sauna-enquire-btn"
              className="btn-secondary mt-8"
            >
              Ask about sauna sessions <ArrowUpRight size={14} />
            </Link>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="grid grid-cols-2 gap-4">
              <img src="/images/rx4/sauna-trailer.jpg" alt="The RX4 Wild Sauna trailer by the sea" className="rounded-2xl border border-white/10 w-full aspect-[4/3] object-cover" loading="lazy" data-testid="sauna-photo-trailer" />
              <img src="/images/rx4/sauna-inside.jpg" alt="Inside the wood-fired wild sauna" className="rounded-2xl border border-white/10 w-full aspect-[4/3] object-cover mt-8" loading="lazy" data-testid="sauna-photo-inside" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* WHY */}
    <section className="bg-ink-soft border-t border-white/5" data-testid="why-events-section">
      <div className="section-shell">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <Reveal className="lg:col-span-5">
            <p className="overline-label mb-4">Why choose our events?</p>
            <blockquote className="font-display text-2xl sm:text-3xl font-medium leading-relaxed text-bone border-l-2 border-brand pl-6">
              "I don't think you'd get a better experience than the one I've had, for the money that you invest."
            </blockquote>
          </Reveal>
          <div className="lg:col-span-7 space-y-0">
            {whyUs.map((w, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div data-testid={`why-event-${i}`} className="flex items-start gap-3 text-slate-300 border-b border-white/10 last:border-b-0 py-5">
                  <Check size={18} className="text-brand mt-1 shrink-0" strokeWidth={2.5} /> {w}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  </main>
);

export default Events;
